import React, { useCallback, useEffect, useState } from 'react';

import { useAnyPermission } from '../../context/PermissionContext';
import { ADMISSION_PERMISSIONS, DISCHARGE_PERMISSIONS } from '../../constants/admission';
import { listLabOrdersForVisit } from '../../services/api/labApi';
import DiagnosticsCard from '../admission/diagnostics/DiagnosticsCard';
import { DIAGNOSTICS_ROW_STATE } from '../admission/diagnostics/diagnosticsModel';
import OrderLabDialog from '../admission/OrderLabDialog';
import useDiagnosticsBillPrint from '../../hooks/useDiagnosticsBillPrint';
import { diagnosticsBillLabel } from '../../hooks/useDiagnosticsBillPrint';

/**
 * DiagnosticsView (C6a/D30) — the IPD page's ONLY child today: "Diagnostics"
 * (D32 naming — never "LabView"/"Lab view" in new code). Knows NOTHING about
 * finding a patient — it takes one prop, `admission`, and renders the SAME
 * `DiagnosticsCard` / `OrderLabDialog` the Manage Admission tab mounts (D29).
 * No copy, no fork, no `variant` prop — the guard test
 * (`src/__tests__/labSurfaceSingleHome.test.js`) fails if a second copy of
 * either ever appears.
 *
 * `DiagnosticsCard` mounts no dialog itself (`onAction('orderLab', admission)`
 * is its whole contract) — exactly as `AdmissionDeskPage` does, this view
 * owns its OWN `OrderLabDialog` mount and its OWN monotonic
 * `diagnosticsRefreshToken` (never threaded down from the IpdPage framework —
 * the framework knows nothing about diagnostics, D30).
 *
 * "Generate Diagnostics bill" enables from a SEPARATE tiny read of this
 * stay's own lab orders (`listLabOrdersForVisit` — no money field on it,
 * confirmed in the C6+C7 build-plan's hard-rule table) — NEVER from
 * `getRunningBill`'s `has_diagnostics` (money-bearing, not called on this
 * page at all). "Any non-cancelled row" is billable, mirroring
 * `RunningBillPanel`'s `diagnosticsBillDisabled` rule without importing it.
 */
export default function DiagnosticsView({ admission }) {
  const admissionId = admission.admission_id;
  // QA-CTF-1 — own slug OR ACCESS_IPD, mirroring the backend's
  // require_any_permission(GENERATE_IPD_BILL_PDF, ACCESS_IPD) on
  // GET /admissions/{id}/lab-bill/pdf (organisation_admission_discharge.py).
  // Same reasoning as DiagnosticsCard's order-lab gate: GENERATE_IPD_BILL_PDF
  // is stripped by the same per-member Manage-Admission revoke that ACCESS_IPD
  // is designed to survive (D27). Safe on the Manage Admission mount too —
  // NOT because that tab is route-guarded (it isn't) but because `GET
  // /admissions/{id}` stays VIEW_ADMISSION-only, so a member who kept only
  // ACCESS_IPD 403s fetching the admission itself and this view never
  // mounts there to show the widened button (see DiagnosticsCard.jsx).
  const canPrintBill = useAnyPermission(
    DISCHARGE_PERMISSIONS.GENERATE_IPD_BILL_PDF,
    ADMISSION_PERMISSIONS.ACCESS_IPD,
  );

  // C2's pattern, reused verbatim (own copy, own name — the framework never
  // sees this token): a monotonic integer, bumped once per successful order,
  // so it can only ever cause exactly one extra fetch and can never re-fire
  // on a plain re-render.
  const [diagnosticsRefreshToken, setDiagnosticsRefreshToken] = useState(0);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);

  const [hasBillableOrder, setHasBillableOrder] = useState(false);
  useEffect(() => {
    let alive = true;
    listLabOrdersForVisit(`labadm_${admissionId}`).then((res) => {
      if (!alive) return;
      const orders = res.success ? (res.data || []) : [];
      setHasBillableOrder(orders.some((o) => o.status !== DIAGNOSTICS_ROW_STATE.CANCELLED));
    });
    return () => { alive = false; };
  }, [admissionId, diagnosticsRefreshToken]);

  // F1 — the ONE shared print helper (confirm-before-mint, printing flag,
  // error mapping). Never a forked copy of this logic. Status is read fresh
  // at click time (`generate(admission.status)`), never bound at mount.
  const {
    generate: generateBill,
    printing: printingBill,
    error: billError,
    confirmModal,
  } = useDiagnosticsBillPrint(admissionId);
  const handleGenerateBill = useCallback(() => generateBill(admission.status), [generateBill, admission.status]);

  // DiagnosticsCard's whole dialog contract — it mounts no dialog itself.
  const handleCardAction = useCallback((kind) => {
    if (kind === 'orderLab') setOrderDialogOpen(true);
  }, []);

  return (
    <div data-testid={`ipd-diagnostics-view-${admissionId}`}>
      {canPrintBill && (
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={handleGenerateBill}
            disabled={printingBill || !hasBillableOrder}
            className="px-3 py-1.5 rounded-lg border border-teal-600 dark:border-teal-500 bg-white dark:bg-slate-800 text-teal-700 dark:text-teal-300 text-[12.5px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="ipd-generate-diagnostics-bill"
            title={hasBillableOrder ? 'Generate the consolidated diagnostics bill' : 'No diagnostics to bill'}
          >{printingBill ? 'Generating…' : diagnosticsBillLabel(admission.status)}</button>
        </div>
      )}
      {billError && (
        <div className="mb-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="ipd-bill-error">
          {billError}
        </div>
      )}

      <DiagnosticsCard
        admission={admission}
        onAction={handleCardAction}
        diagnosticsRefreshToken={diagnosticsRefreshToken}
      />

      {orderDialogOpen && (
        <OrderLabDialog
          admission={admission}
          onClose={() => setOrderDialogOpen(false)}
          onOrdered={() => setDiagnosticsRefreshToken((n) => n + 1)}
        />
      )}
      {confirmModal}
    </div>
  );
}

